"use client";

// French hero for widget + platform pages (FR-1). Copy of
// widgetHeroLightFr.comp.tsx with the English hero's platform parts: the
// platform logo above the H1, the platform icon on the image, "<widget> pour
// <platform>" as the H1 and the platform slug on both events. Same rules as
// the hub version: no quotes strip, no PostHog CTA-text experiment.
import React, { useEffect } from "react";
import Image from "next/image";
import { StyledWidgetHeroLight } from "../widgetHeroLight/widgetHeroLight.styles";
import { IPlatform, IPlugin } from "../../types";
import { CustomLink } from "../link/link.comp";
import { trackEvent } from "@/lib/track";
import { ParallaxEditor } from "../parallax-editor/parallax-editor.comp";
import { CommonNinjaWidgetRenderer } from "../CommonNinjaWidgetRenderer/commonNinjaWidgetRenderer.comp";
import { createMobileAwareClickHandler } from "../../helpers/mobile-redirect";
import { HeaderFr } from "./headerFr.comp";
import { WidgetSpokeNavbarFr } from "./widgetSpokeNavbarFr.comp";
import { SocialProofFr } from "./socialProofFr.comp";

interface IWidgetSpokeHeroFrProps {
  widget: IPlugin;
  platform: IPlatform;
  widgetImgSrc: string;
  showTemplatesTab?: boolean;
}

export const WidgetSpokeHeroFr = ({
  widget,
  platform,
  widgetImgSrc,
  showTemplatesTab,
}: IWidgetSpokeHeroFrProps) => {
  useEffect(() => {
    trackEvent(`Enter App Page`, {
      serviceName: widget.serviceName,
      platform: platform.slug,
      locale: "fr",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editorPath = `/${widget.slug}/editor`;

  return (
    <StyledWidgetHeroLight>
      <HeaderFr lightMode />
      <div className="navbar-wrapper">
        <WidgetSpokeNavbarFr
          widget={widget}
          platform={platform}
          active="overview"
          showTemplatesTab={showTemplatesTab}
        />
      </div>
      <div className="hero-wrapper">
        <div className="hero-text-wrapper">
          <div className="hero-platform-logo">
            <Image src={platform.logo} alt={platform.title} fill />
          </div>
          <h1>{`${widget.name} pour ${platform.title}`}</h1>
          <h2>{widget.teaser}</h2>
          <div className="button-wrapper">
            <CustomLink
              variant="gradient"
              external
              title={`${widget.buttonText} pour ${platform.title}`}
              className="try-button"
              id="main-create-widget-cta"
              href={editorPath}
              onClick={createMobileAwareClickHandler(() => {
                // Same event and payload as the English hero (DEV-15194), plus
                // locale, so French clicks add up with English ones.
                trackEvent("widget_hero_cta_click", {
                  widget: widget.slug,
                  serviceName: widget.serviceName,
                  platform: platform.slug,
                  cta_variant: "control",
                  locale: "fr",
                });
              }, editorPath)}
            >
              {widget.buttonText}
            </CustomLink>
            <SocialProofFr />
          </div>
          <p
            className="cn-editor-language-note"
            style={{ fontSize: 13, opacity: 0.75, margin: "10px 0 0" }}
          >
            L&apos;éditeur est en anglais pour le moment. Sans carte bancaire.
          </p>
        </div>
        <div className="hero-img-wrapper">
          {platform.icon && (
            <div className="platform-img-wrapper">
              <Image src={platform.icon} alt={platform.title} fill />
            </div>
          )}
          <ParallaxEditor
            widgetImgSrc={widgetImgSrc}
            editorContainerImgSrc="https://website-assets.commoninja.com/distribution/widget-light-hero-editor.webp"
            wrapperStyle={{ overflow: "visible" }}
            style={{ width: "100%" }}
            widgetImgStyle={{ width: "805px", height: "auto", top: "10%", left: "-15%" }}
            editorContainerImgStyle={{ width: "770px", height: "620px", transform: "translate(-20%, 0)" }}
            contentImgStyle={{ top: "38%" }}
            skinsImgStyle={{ top: "17%", left: "78%" }}
            hideContentImg
            hideLayoutsImg
            hideSkinsImg
          />
        </div>
      </div>
      <CommonNinjaWidgetRenderer
        widgetId={"87cf32fd-d8c2-4a3b-8cfc-0f419c000fbf"}
        style={{ filter: "brightness(0)" }}
      />
    </StyledWidgetHeroLight>
  );
};
